import { useTranslation } from 'react-i18next';
import { Loader2, ScrollText, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { isAccountEmpty, useWhy } from '@/lib/why/use-why';
import type { WhyScope } from '@/lib/why/types';
import { WhyAccount } from './why-account';
import { EvidencePanel } from './why-evidence';
import { ChapterCard } from './why-chapter';

/**
 * "¿Por qué?" — the account on top, the evidence underneath (slice W6 /
 * NEX-513). The operator reads what happened and can verify every claim
 * without leaving the dialog.
 *
 * CONTROLLED BY THE PARENT (`<Dialog open …>`, no `DialogTrigger`) — the house
 * pattern of `hotel-desk/outcome-dialog.tsx`, so the surface that opens it also
 * owns when it closes.
 *
 * FOUR STATES, ALL EXPLICIT — loading, error, empty, success — and two of them
 * are NOT errors and must never be dressed as one:
 *
 *   · EMPTY: the journal is readable and holds nothing yet. The evidence panel
 *     still renders, because it is live and answers the question on its own.
 *   · an unreadable ACCOUNT (`narrative.known:false`) or an unnarrated CHAPTER
 *     (`narration:null`) — each says, in words, what is missing and why, and
 *     never substitutes a sentence for prose that was never grounded.
 *
 * The ERROR state is only the READ failing (network / 401 / 5xx): the BFF
 * degrades open, so a 200 is never an error however empty it is. It renders
 * inline and the dialog STAYS OPEN, with a retry.
 *
 * NO POLLING. One read on open; `NarrativeChanged@v1` on the `disruption:<urn>`
 * room keeps it live (see `lib/why/use-why.ts`).
 */
export function WhyDialog({ scope, onClose }: { scope: WhyScope; onClose: () => void }) {
  const { t } = useTranslation('why');
  const query = useWhy(scope);
  const view = query.data;

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-h-[85vh] w-[min(56rem,95vw)] max-w-3xl grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScrollText className="h-4 w-4 shrink-0" aria-hidden />
            {t('dialog.title')}
          </DialogTitle>
          <DialogDescription>
            {scope.kind === 'case' ? t('dialog.subtitle.case') : t('dialog.subtitle.disruption')}
          </DialogDescription>
        </DialogHeader>

        {/* Contained scroll (frontend-conventions §18.1): the body scrolls, the
            page never does, and the header/footer stay put. */}
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1" data-testid="why-dialog-body">
          {query.isPending ? (
            <p
              className="flex items-center gap-2 py-6 text-xs text-muted-foreground"
              data-testid="why-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t('loading')}
            </p>
          ) : query.isError || !view ? (
            <div className="py-4" data-testid="why-error">
              <p className="flex items-start gap-1.5 text-xs text-destructive">
                <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>
                  <span className="font-semibold">{t('error.title')}</span>{' '}
                  <span className="text-muted-foreground">{t('error.body')}</span>
                </span>
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => void query.refetch()}
                disabled={query.isFetching}
                data-testid="why-retry"
              >
                {query.isFetching ? (
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                ) : null}
                {t('error.retry')}
              </Button>
            </div>
          ) : (
            <>
              {isAccountEmpty(view) && view.narrative?.known ? (
                <section data-testid="why-empty">
                  <h3 className="text-sm font-semibold text-foreground">{t('empty.title')}</h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t('empty.body')}</p>
                </section>
              ) : (
                <WhyAccount view={view} />
              )}

              <EvidencePanel evidence={view.evidence} />

              {view.chapters.length ? (
                <section data-testid="why-chapters">
                  <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('chapters.heading', { count: view.chapters.length })}
                  </h3>
                  <div className="space-y-2">
                    {view.chapters.map((chapter) => (
                      <ChapterCard key={chapter.chapterId} chapter={chapter} />
                    ))}
                  </div>
                </section>
              ) : null}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="why-close">
            {t('dialog.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
